import { Gamepad2, Cpu, Laptop, HardDrive, ShieldCheck, Wrench, ArrowUpRight, Check, X } from "lucide-react";

// `prefill` maps to BookingForm fields. `covered` / `notCovered` list explicit
// services so customers know upfront exactly what I do and don't repair.
const SERVICES = [
  {
    icon: Gamepad2,
    name: "Console Repair",
    desc: "PlayStation, Xbox, Nintendo Switch, Steam Deck, retro consoles. Drop-off or mail-in welcome.",
    tag: "DROP-OFF · MAIL-IN",
    accent: "#0055FF",
    span: "lg:col-span-2",
    prefill: {},
    covered: [
      "Disc drive repair / replacement",
      "Hard drive / SSD replacement",
      "General cleaning & thermal paste",
      "Power supply (case by case)",
      "Controller repair (drift, buttons, shells)",
    ],
    notCovered: [
      "No PCB / motherboard-level soldering",
      "HDMI port work — not currently offered",
    ],
  },
  {
    icon: Cpu,
    name: "PC Repair",
    desc: "Desktop diagnostics, component swaps, thermal paste, full rebuilds. In-person only.",
    tag: "IN-PERSON ONLY",
    accent: "#FFCC00",
    prefill: { device: "Desktop PC", delivery: "drop-off" },
    covered: [
      "GPU / PSU / RAM / SSD upgrades",
      "Thermal paste & deep cleaning",
      "Full rebuilds & custom builds",
      "Diagnostics (no-boot, BSODs, crashes)",
      "Anything non-PCB — fair game",
    ],
    notCovered: ["No motherboard soldering / trace repair"],
  },
  {
    icon: Laptop,
    name: "Laptop Repair",
    desc: "Make and model dependent. Parts availability varies — I'll confirm before quoting.",
    tag: "BY MODEL",
    accent: "#0055FF",
    prefill: { device: "Laptop" },
    covered: [
      "Hard drive / SSD replacement",
      "Battery replacement",
      "Screen replacement (when part available)",
      "Keyboard replacement (when part available)",
      "Disc drive replacement",
    ],
    notCovered: ["No motherboard / PCB repair"],
  },
  {
    icon: Wrench,
    name: "Controller Repair",
    desc: "Stick drift, broken triggers, sticky buttons. Bring it back to life — flat $30.",
    tag: "FLAT $30",
    accent: "#FFCC00",
    prefill: { device: "Controller", service: "Controller Repair" },
    covered: [
      "Stick drift fixes (all brands)",
      "Button / trigger replacement",
      "Shell & grip swaps",
    ],
  },
  {
    icon: HardDrive,
    name: "Storage Upgrades",
    desc: "HDD → SSD migrations, NVMe upgrades, data transfers. Faster boots, longer life.",
    tag: "UPGRADE",
    accent: "#34C759",
    prefill: { service: "Hard Drive / SSD Replacement" },
    covered: [
      "HDD → SSD migration with data transfer",
      "NVMe upgrades (PC & PS5)",
      "Clean Windows reinstalls",
    ],
  },
  {
    icon: ShieldCheck,
    name: "Diagnostic First",
    desc: "Always diagnose before you pay. You approve the quote — then I get to work.",
    tag: "NO-SURPRISE",
    accent: "#0055FF",
    prefill: { service: "Diagnostic / Other" },
    covered: [
      "Free diagnostic on drop-off / in-person jobs",
      "Ship-in jobs: $30 deposit applied toward repair (or $15 diagnostic fee kept if you decline)",
      "Upfront quote before any work",
      "Honest \"not worth fixing\" calls when needed",
    ],
  },
];

function buildBookingHref(prefill) {
  const params = new URLSearchParams();
  if (prefill?.device) params.set("device", prefill.device);
  if (prefill?.service) params.set("service", prefill.service);
  if (prefill?.delivery) params.set("delivery", prefill.delivery);
  const qs = params.toString();
  return qs ? `/?${qs}#booking` : "/#booking";
}

export default function Services() {
  return (
    <section id="services" className="relative py-24 sm:py-32 border-t border-white/5" data-testid="services-section">
      <div className="max-w-7xl mx-auto px-6 sm:px-8">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-16">
          <div>
            <div className="font-mono text-xs tracking-[0.25em] uppercase text-zinc-500 mb-4">
              [ 01 ] · WHAT I REPAIR
            </div>
            <h2 className="font-display font-bold text-4xl sm:text-5xl lg:text-6xl tracking-tighter">
              Services built around <span className="text-[#0055FF]">your hardware</span>.
            </h2>
          </div>
          <p className="text-zinc-400 max-w-md leading-relaxed">
            Every card below lists exactly what's covered so you know upfront what I can and can't fix.
            Diagnostics are free for drop-off / in-person jobs — ship-ins require a $30 deposit that's applied toward your repair.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {SERVICES.map((s) => {
            const Icon = s.icon;
            return (
              <a
                key={s.name}
                href={buildBookingHref(s.prefill)}
                className={`tech-card group relative bg-[#121212] border border-zinc-800 rounded-md p-8 overflow-hidden block focus:outline-none focus:ring-2 focus:ring-[#0055FF] ${s.span || ""}`}
                data-testid={`service-card-${s.name.toLowerCase().replace(/\s+/g, '-')}`}
              >
                <div
                  className="absolute left-0 top-0 bottom-0 w-[2px] opacity-0 group-hover:opacity-100 transition-opacity"
                  style={{ background: s.accent }}
                />
                <div className="flex items-start justify-between mb-6">
                  <div
                    className="w-11 h-11 rounded-md border border-zinc-800 flex items-center justify-center"
                    style={{ background: "#1A1A1A" }}
                  >
                    <Icon className="w-5 h-5" style={{ color: s.accent }} />
                  </div>
                  <span
                    className="font-mono text-[10px] tracking-[0.2em] uppercase px-2 py-1 border border-zinc-800 rounded-sm"
                    style={{ color: s.accent }}
                  >
                    {s.tag}
                  </span>
                </div>
                <h3 className="font-display font-semibold text-2xl mb-3 tracking-tight flex items-center gap-2">
                  {s.name}
                  <ArrowUpRight
                    className="w-5 h-5 text-zinc-600 group-hover:text-white group-hover:-translate-y-0.5 group-hover:translate-x-0.5 transition-all"
                  />
                </h3>
                <p className="text-zinc-400 leading-relaxed mb-5">{s.desc}</p>

                {s.covered && s.covered.length > 0 && (
                  <ul className="space-y-1.5 mb-3">
                    {s.covered.map((item) => (
                      <li key={item} className="flex items-start gap-2 text-sm text-zinc-300">
                        <Check className="w-3.5 h-3.5 text-[#34C759] mt-0.5 flex-shrink-0" />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                )}

                {s.notCovered && s.notCovered.length > 0 && (
                  <ul className="space-y-1.5 mb-3 pt-3 border-t border-zinc-800/80">
                    {s.notCovered.map((item) => (
                      <li key={item} className="flex items-start gap-2 text-sm text-zinc-500">
                        <X className="w-3.5 h-3.5 text-[#FF3B30] mt-0.5 flex-shrink-0" />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                )}

                <div className="mt-5 font-mono text-[10px] tracking-[0.2em] uppercase text-zinc-500 group-hover:text-white transition-colors">
                  Book this repair →
                </div>
              </a>
            );
          })}
        </div>
      </div>
    </section>
  );
}
